import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { changePassword } from '../api/auth';
import classNames from 'classnames';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const ChangePasswordDialog: React.FC<Props> = ({ open, onClose, onSuccess }) => {
  const { t } = useTranslation();

  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [newPwd2, setNewPwd2] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  if (!open) return null;

  // 前端密码策略（与后端一致）：≥8、禁止空白、大小写数字与符号至少各1
  const pwPolicyRe = /^(?!.*\s)(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\w\s]).{8,}$/;
  const pwPolicyOk = pwPolicyRe.test(newPwd);

  const canSubmit =
    !!currentPwd && !!newPwd && !!newPwd2 && newPwd === newPwd2 && pwPolicyOk && !loading;

  const getErrDetail = (e: any) =>
    e?.response?.data?.detail ?? e?.detail ?? e?.data?.detail ?? e;

  const handleSubmit = async (evt: React.FormEvent) => {
    evt.preventDefault();
    setErr(null);
    setOk(false);
    setLoading(true);
    try {
      await changePassword(currentPwd, newPwd, newPwd2);
      setOk(true);
      setCurrentPwd('');
      setNewPwd('');
      setNewPwd2('');
      onSuccess?.();
    } catch (e: any) {
      const d = getErrDetail(e);
      const code =
        (typeof d === 'object' ? d.code || d.status || d.error : undefined) ||
        (typeof d === 'string' ? d : undefined);

      if (code === 'current_password_incorrect' || /当前密码不正确/.test(String(code))) {
        setErr(t('password.current_incorrect'));
      } else if (code === 'password_reused' || /相同/.test(String(code))) {
        setErr(t('password.reused'));
      } else if (code === 'password_mismatch' || /不一致/.test(String(code))) {
        setErr(t('password.mismatch'));
      } else if (code === 'PASSWORD_NO_WHITESPACE') {
        setErr(t('password.no_whitespace'));
      } else if (
        code === 'PASSWORD_TOO_SHORT' ||
        code === 'PASSWORD_NEEDS_UPPER' ||
        code === 'PASSWORD_NEEDS_LOWER' ||
        code === 'PASSWORD_NEEDS_NUMBER' ||
        code === 'PASSWORD_NEEDS_SPECIAL' ||
        code === 'weak_password' ||
        /policy/i.test(String(code))
      ) {
        setErr(t('password.weak_policy'));
      } else {
        // 尝试拿 message；否则用通用错误
        const msg = typeof d === 'object' && d?.message ? d.message : null;
        setErr(msg || t('common.error'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-6">
        <h2 className="text-lg font-semibold mb-4">{t('password.change')}</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* 当前密码 */}
          <div>
            <label className="block text-sm mb-1">{t('password.current')}</label>
            <input
              type="password"
              autoComplete="current-password"
              className="w-full border rounded-lg px-3 py-2"
              value={currentPwd}
              onChange={(e) => setCurrentPwd(e.target.value)}
              required
            />
          </div>

          {/* 新密码 */}
          <div>
            <label className="block text-sm mb-1">{t('password.new')}</label>
            <input
              type="password"
              autoComplete="new-password"
              className="w-full border rounded-lg px-3 py-2"
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              required
            />
            <p className="text-xs text-gray-500 mt-1">{t('password.policy_hint')}</p>
          </div>

          {/* 确认新密码 */}
          <div>
            <label className="block text-sm mb-1">{t('password.new_confirm')}</label>
            <input
              type="password"
              autoComplete="new-password"
              className="w-full border rounded-lg px-3 py-2"
              value={newPwd2}
              onChange={(e) => setNewPwd2(e.target.value)}
              required
            />
            {newPwd2 && newPwd !== newPwd2 && (
              <p className="text-xs text-red-600 mt-1">{t('password.mismatch')}</p>
            )}
            {newPwd && !pwPolicyOk && (
              <p className="text-xs text-red-600 mt-1">{t('password.weak_policy')}</p>
            )}
          </div>

          {/* 错误/成功提示 */}
          {err && <p className="text-sm text-red-600">{err}</p>}
          {ok && <p className="text-sm text-green-600">{t('password.changed_ok')}</p>}

          {/* 操作按钮 */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-100"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className={classNames(
                'px-4 py-2 rounded-lg text-sm text-white',
                canSubmit ? 'bg-coral hover:opacity-90' : 'bg-gray-400 cursor-not-allowed'
              )}
            >
              {loading ? t('common.saving') : t('password.change')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordDialog;
